import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/** Adds the countdown cursor, keyboard and editor behavior to the native slider. */
class HenryHero extends Component {
  #controller;
  #observer;
  #viewportObserver;
  #slider;
  #cursor;
  #progress;
  #countdown;
  #countdownSlide;
  #readiness;
  #resizeFrame = 0;
  #pointerPosition;
  #inViewport = false;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  #pointer = matchMedia('(hover: hover) and (pointer: fine)');

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#setup();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  async #setup() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    await customElements.whenDefined('slideshow-component');
    if (signal.aborted || !this.isConnected) return;
    this.#slider = this.querySelector('slideshow-component');
    if (!this.#slider) return;

    this.#cursor = this.querySelector('.pd-sliding-hero__cursor');
    this.#progress = this.querySelector('.pd-sliding-hero__cursor-progress');
    this.#countdownSlide = this.#slider.slides?.[this.#slider.current];
    const bounds = this.getBoundingClientRect();
    this.#inViewport = bounds.bottom > 0 && bounds.top < innerHeight;
    this.#observer = new MutationObserver((records) => {
      if (records.some(({ attributeName }) => ['src', 'srcset', 'sizes', 'media'].includes(attributeName))) {
        this.#cancelReadiness();
      }
      this.#syncCountdown();
    });
    this.#observer.observe(this.#slider, {
      attributes: true,
      subtree: true,
      attributeFilter: ['paused', 'transitioning', 'dragging', 'src', 'srcset', 'sizes', 'media'],
    });
    this.#viewportObserver = new IntersectionObserver(([entry]) => {
      this.#inViewport = entry.isIntersecting;
      if (!this.#inViewport) this.#hideCursor();
      this.#syncCountdown();
    });
    this.#viewportObserver.observe(this);

    // Invalidate before a manual selection starts: looping can await scrolling
    // before the slider emits its selection event. Do not intercept the control.
    const restartForControl = (event) => {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      const control = event.target instanceof Element ? event.target.closest('button') : null;
      const action = control?.getAttribute('on:click');
      if (action === '/next' || action === '/previous' || action?.startsWith('/select/')) this.#restartCountdown();
    };
    for (const type of ['pointerdown', 'keydown', 'click']) {
      this.addEventListener(type, restartForControl, { signal, capture: true });
    }
    window.addEventListener('resize', () => {
      this.#cancelReadiness();
      cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = requestAnimationFrame(() => {
        this.#resizeFrame = 0;
        this.#syncCountdown();
      });
    }, { signal, passive: true });
    this.addEventListener('load', (event) => {
      const pending = this.#readiness;
      if (!pending) return;
      if ((event.target === pending.currentImage && pending.currentSource !== undefined &&
        pending.currentImage.currentSrc !== pending.currentSource) ||
        (event.target === pending.nextImage && pending.nextSource !== undefined &&
        pending.nextImage.currentSrc !== pending.nextSource)) {
        this.#cancelReadiness();
        this.#syncCountdown();
      }
    }, { signal, capture: true });

    // Keep keyboard focus on the visible next control after advancing a slide.
    this.#slider.addEventListener(SlideshowSelectEvent.eventName, (event) => {
      if (!(event instanceof SlideshowSelectEvent) || event.target !== this.#slider) return;
      if (event.detail.slide !== this.#countdownSlide) {
        this.#countdownSlide = event.detail.slide;
        this.#restartCountdown();
      }
      const focused = document.activeElement;
      if (!this.contains(focused) || !focused?.matches('.pd-sliding-hero__next')) return;
      event.detail.slide.querySelector('.pd-sliding-hero__next')?.focus({ preventScroll: true });
    }, { signal });

    this.addEventListener('focusin', (event) => {
      // Mouse clicks can restart the countdown; keyboard readers get a still slide.
      if (event.target instanceof Element && event.target.matches(':focus-visible')) this.#slider.pause();
    }, { signal });
    const respectMotion = () => {
      if (this.#motion.matches) this.#slider.pause();
    };
    this.#motion.addEventListener('change', respectMotion, { signal });
    respectMotion();
    this.#restartCountdown();

    this.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'touch') {
        this.#hideCursor();
        return;
      }
      this.#pointerPosition = { x: event.clientX, y: event.clientY };
      this.#positionCursor();
    }, { signal, passive: true });
    this.addEventListener('pointerleave', () => this.#hideCursor(), { signal });
    this.addEventListener('pointerout', () => this.#positionCursor(), { signal });
    this.#pointer.addEventListener('change', () => this.#hideCursor(), { signal });
    window.addEventListener('scroll', () => this.#hideCursor(), { signal, passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#hideCursor();
      this.#syncCountdown();
    }, { signal });

    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;
      if (!(block instanceof Element) || !this.contains(block)) return;
      const slide = block.closest('slideshow-slide');
      if (!slide) return;
      this.#slider.pause();
      this.#slider.select({ id: slide.getAttribute('slide-id') }, undefined, { animate: false });
    }, { signal });
  }

  #restartCountdown() {
    this.#cancelReadiness();
    this.#countdown?.cancel();
    this.#countdown = undefined;
    const duration = Number(this.dataset.autoplaySpeed) * 1000;
    if (!this.#progress || this.dataset.autoplay !== 'true' || !Number.isFinite(duration) || duration <= 0) {
      this.#syncCountdown();
      return;
    }

    // This animation is the timer: the ring and slide advance cannot drift apart.
    this.#countdown = this.#progress.animate(
      [{ strokeDashoffset: '100' }, { strokeDashoffset: '0' }],
      { duration, easing: 'linear', fill: 'forwards' }
    );
    this.#countdown.onfinish = () => {
      this.#advanceWhenReady();
    };
    this.#syncCountdown();
  }

  #syncCountdown() {
    const paused = this.#isPaused();
    this.#slider.setAttribute('aria-live', this.#countdown && !paused ? 'off' : 'polite');
    this.#positionCursor();
    if (paused) this.#cancelReadiness();
    else if (this.#countdown && !this.#resizeFrame) this.#prepareNext();
    if (!this.#countdown || this.#countdown.playState === 'finished') return;
    if (paused) this.#countdown.pause();
    else if (this.#countdown.playState === 'paused') this.#countdown.play();
  }

  #isPaused() {
    return document.hidden || !this.#inViewport || this.#motion.matches || this.#slider.paused ||
      this.#slider.hasAttribute('transitioning') || this.#slider.hasAttribute('dragging');
  }

  #nextSlide() {
    const slides = this.#slider.slides;
    return slides?.length > 1 ? slides[this.#slider.nextIndex % slides.length] : undefined;
  }

  #cancelReadiness() {
    this.#readiness?.controller.abort();
    this.#readiness = undefined;
  }

  #prepareNext() {
    if (this.#readiness) {
      this.#advanceWhenReady();
      return;
    }
    const current = this.#slider.slides?.[this.#slider.current];
    const next = this.#nextSlide();
    if (!current || !next) return;
    const pending = this.#readiness = {
      controller: new AbortController(), current, next,
      currentImage: current.querySelector('picture img'),
      nextImage: next.querySelector('picture img'),
      currentSource: undefined, nextSource: undefined, ready: false,
    };
    const { signal } = pending.controller;
    // Use the existing <picture> image so the browser chooses the correct
    // mobile/desktop source. Only warm the next slide after the visible image
    // has decoded (or failed), retaining its existing low fetch priority.
    this.#imageReady(pending.currentImage, signal).then(async (source) => {
      if (!this.#isCurrentReadiness(pending)) return;
      pending.currentSource = source ?? pending.currentImage?.currentSrc ?? '';
      if (pending.nextImage) pending.nextImage.loading = 'eager';
      pending.nextSource = await this.#imageReady(pending.nextImage, signal);
      if (pending.nextSource === undefined || !this.#isCurrentReadiness(pending)) return;
      pending.ready = true;
      this.#advanceWhenReady();
    }).catch(() => {
      // A failed image keeps the current slide visible. Manual controls remain
      // available; a later selection, source change or resume can try again.
    });
  }

  #isCurrentReadiness(pending) {
    return this.isConnected && !this.#controller.signal.aborted && !pending.controller.signal.aborted &&
      this.#readiness === pending && this.#slider.slides?.[this.#slider.current] === pending.current &&
      this.#nextSlide() === pending.next;
  }

  #advanceWhenReady() {
    const pending = this.#readiness;
    if (!pending?.ready || !this.#isCurrentReadiness(pending) || this.#isPaused() || this.#resizeFrame ||
      this.#countdown?.playState !== 'finished') return;
    if ((pending.currentImage && pending.currentImage.currentSrc !== pending.currentSource) ||
      (pending.nextImage && (!pending.nextImage.complete || !pending.nextImage.naturalWidth ||
        pending.nextImage.currentSrc !== pending.nextSource))) {
      this.#cancelReadiness();
      this.#prepareNext();
      return;
    }
    // The finished ring holds while waiting; only this timer's ready target can
    // advance. Clear it before next(), whose selection can complete asynchronously.
    pending.ready = false;
    this.#slider.next();
  }

  /** Resolve only once the currently selected responsive source has decoded. */
  #imageReady(image, signal) {
    if (!image) return Promise.resolve('');
    return new Promise((resolve) => {
      let decoding = false;
      const finish = (source) => {
        image.removeEventListener('load', check);
        image.removeEventListener('error', failed);
        signal.removeEventListener('abort', failed);
        resolve(source);
      };
      const failed = () => finish(undefined);
      const check = () => {
        if (signal.aborted) return failed();
        if (!image.complete || decoding) return;
        if (!image.naturalWidth || !image.currentSrc) return failed();
        const source = image.currentSrc;
        decoding = true;
        image.decode().then(() => {
          decoding = false;
          if (signal.aborted) return failed();
          if (image.currentSrc !== source || !image.complete) return check();
          finish(image.naturalWidth ? source : undefined);
        }).catch(() => {
          decoding = false;
          if (signal.aborted) return failed();
          // Switching a responsive source can reject an old decode even when
          // the new image is healthy. Wait for that chosen source instead.
          if (image.currentSrc !== source || !image.complete) return check();
          failed();
        });
      };
      image.addEventListener('load', check);
      image.addEventListener('error', failed);
      signal.addEventListener('abort', failed, { once: true });
      check();
    });
  }

  #positionCursor() {
    if (!this.#cursor || !this.#pointerPosition || !this.#pointer.matches) {
      this.removeAttribute('data-cursor-active');
      return;
    }
    const { x, y } = this.#pointerPosition;
    const target = document.elementFromPoint(x, y);
    const active = this.contains(target) && Boolean(target?.closest('.pd-sliding-hero__next'));
    this.toggleAttribute('data-cursor-active', active);
    if (!active) return;
    const rect = this.getBoundingClientRect();
    this.#cursor.style.setProperty('--pd-cursor-x', `${x - rect.left - 28}px`);
    this.#cursor.style.setProperty('--pd-cursor-y', `${y - rect.top - 28}px`);
  }

  #hideCursor() {
    this.#pointerPosition = undefined;
    this.removeAttribute('data-cursor-active');
  }

  #cleanup() {
    this.#cancelReadiness();
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = 0;
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#viewportObserver?.disconnect();
    this.#countdown?.cancel();
    this.#countdown = undefined;
    this.#hideCursor();
    this.#slider?.suspend();
  }
}

if (!customElements.get('henry-hero')) customElements.define('henry-hero', HenryHero);
